import { useState, useEffect, memo, ElementType, ComponentPropsWithoutRef } from 'react';
import { useTranslation } from '@/contexts/TranslationContext';
import { normalizeTranslationValue } from '@/lib/translation/translationCache';

type TranslatedTextProps<T extends ElementType = 'span'> = {
  children: string;
  as?: T;
  cacheKey?: string;
} & Omit<ComponentPropsWithoutRef<T>, 'children' | 'as'>;

/**
 * Component that automatically translates its text content
 */
function TranslatedTextInner<T extends ElementType = 'span'>({ 
  children, 
  as,
  className,
  cacheKey,
  ...rest
}: TranslatedTextProps<T>) {
  const { translate, currentLanguage } = useTranslation();
  const [text, setText] = useState(children);
  const Component = as || 'span';

  useEffect(() => {
    let cancelled = false;

    const doTranslate = async () => {
      if (currentLanguage === 'en') {
        setText(children);
        return;
      }
      
      const translated = await translate(children, cacheKey);
      if (!cancelled) {
        setText(normalizeTranslationValue(translated, children));
      }
    };

    doTranslate();

    return () => {
      cancelled = true;
    };
  }, [children, cacheKey, translate, currentLanguage]);

  return (
    <Component className={className} {...rest}>
      {text}
    </Component>
  );
}

export const TranslatedText = memo(TranslatedTextInner) as typeof TranslatedTextInner;

export default TranslatedText;
